import { useState, useEffect } from 'react';
import { useCookies } from "react-cookie";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { updateProfile, setAdmin } from "../../store/profileSlice";
import ReCAPTCHA from "react-google-recaptcha";
import CryptoJS from "crypto-js";
import { 
  Eye, 
  EyeOff, 
  Mail, 
  Lock, 
  Loader2, 
  Users,
  ArrowRight,
  Shield,
  Star,
  Heart,
  CheckCircle,
  LogIn,
  MessageCircle,
  Globe,
  Zap
} from 'lucide-react';

const LoginPage = ({ handleLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [cookie, setCookie] = useCookies(["token"]);
  const navigateTo = useNavigate();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [rememberDevice, setRememberDevice] = useState(false);
  const [emailFocused, setEmailFocused] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);

  const SECRET_KEY = "f3c8a3c9b8a9f0b2440a646f3a5b8f9e6d6e46555a4b2b5c6d7c8d9e0a1b2c3d4f5e6a7b8c9d0e1f2a3b4c5d6e7f8g9h0";

  const encrypt = (text) => CryptoJS.AES.encrypt(text, SECRET_KEY).toString();
  const decrypt = (cipherText) => CryptoJS.AES.decrypt(cipherText, SECRET_KEY).toString(CryptoJS.enc.Utf8);

  useEffect(() => {
    const savedEmail = localStorage.getItem("savedEmail");
    const savedPassword = localStorage.getItem("savedPassword");
    if (savedEmail && savedPassword) {
      setEmail(savedEmail);
      setPassword(decrypt(savedPassword));
      setRememberDevice(true);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (rememberDevice) {
        localStorage.setItem("savedEmail", email);
        localStorage.setItem("savedPassword", encrypt(password));
      } else {
        localStorage.removeItem("savedEmail");
        localStorage.removeItem("savedPassword");
      }

      await new Promise(resolve => setTimeout(resolve, 1000));
      const currentDate = new Date();

      const loginUrl = `${process.env.REACT_APP_API_URL}/alumni/login`;
      const response = await fetch(loginUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password, captchaToken }),
      });

      if (response.ok) {
        const responseData = await response.json();
        handleLogin();

        const { token, alumni } = responseData;
        dispatch(updateProfile(alumni));
        setCookie("token", token, { path: "/" });

        if (alumni.profileLevel === 0) {
          dispatch(setAdmin(true));
        }

        toast.success("Welcome to your community!");
        setLoading(false);

        const currentUrl = window.location.href;
        const loginPath = '/login';
        const baseUrl = currentUrl.endsWith(loginPath)
          ? currentUrl.slice(0, -loginPath.length)
          : currentUrl;

        if (currentUrl.endsWith(loginPath)) {
          window.location.href = baseUrl + "/home";
        } else {
          window.location.href = window.location.href;
        }
      }
        else if (response.status === 404) {
          console.error("User not found");
          toast.error("User not found. Please check your credentials.");
          setLoading(false);
        }
        else if (response.status === 401) {
          console.error("Incorrect password");
          toast.error("Incorrect password. Please check your credentials.");
          setLoading(false);
        }
      else {
        let errorText = "";
        try {
          const errorData = await response.json();
          errorText = errorData.message || "Login failed";
        } catch (parseErr) {
          errorText = "Login failed";
        }
        toast.error(errorText);
        setLoading(false);
      }
    } catch (error) {
      console.error("Login error:", error);
      toast.error("Network error. Please try again.");
      setLoading(false);
    }
  };

  return (
    <div className="h-screen overflow-hidden bg-gradient-to-br from-purple-50/50 via-blue-50/30 to-indigo-50/50">
      {/* Subtle Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-32 -right-24 w-80 h-80 bg-gradient-to-br from-purple-200/30 to-blue-200/20 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-32 -left-24 w-80 h-80 bg-gradient-to-br from-blue-200/20 to-indigo-200/30 rounded-full blur-3xl"></div>
        <div className="absolute top-1/3 left-1/4 w-48 h-48 bg-gradient-to-br from-purple-100/20 to-pink-100/20 rounded-full blur-2xl"></div>
      </div>

      {/* Mobile Layout */}
      <div className="lg:hidden relative h-screen flex flex-col items-center justify-center p-4 z-10">
        {/* Mobile Logo */}
        <div className="mb-6 text-center">
          <div className="w-16 h-16 dynamic-site-bg rounded-3xl flex items-center justify-center mx-auto mb-3 shadow-xl">
            <span className="text-white font-bold text-xl">C</span>
          </div>
          <h1 className="text-2xl font-bold dynamic-site-bg bg-clip-text text-transparent">
            Communiticate
          </h1>
          <p className="text-gray-500 text-sm mt-1">Connect. Build. Thrive.</p>
        </div>

        {/* Mobile Form Container */}
        <div className="w-full max-w-sm bg-white/70 backdrop-blur-2xl rounded-3xl shadow-xl p-6 border border-white/30">
          <div className="text-center space-y-1 mb-6">
            <h2 className="text-lg font-semibold text-gray-800">Welcome Back!</h2>
            <p className="text-gray-500 text-sm">Sign in to your community</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Mobile Email Field */}
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700">Email</label>
              <div className="relative">
                <Mail className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 transition-colors duration-200 ${emailFocused ? 'text-purple-500' : 'text-gray-400'}`} />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onFocus={() => setEmailFocused(true)}
                  onBlur={() => setEmailFocused(false)}
                  placeholder="Enter your email"
                  className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-2xl focus:outline-none focus:border-purple-400 focus:ring-2 focus:ring-purple-400/20 transition-all duration-200 bg-white/80 text-gray-800"
                  required
                />
              </div>
            </div>

            {/* Mobile Password Field */}
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700">Password</label>
              <div className="relative">
                <Lock className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 transition-colors duration-200 ${passwordFocused ? 'text-purple-500' : 'text-gray-400'}`} />
                <input
                  type={passwordVisible ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onFocus={() => setPasswordFocused(true)}
                  onBlur={() => setPasswordFocused(false)}
                  placeholder="Enter your password"
                  className="w-full pl-10 pr-10 py-3 border border-gray-200 rounded-2xl focus:outline-none focus:border-purple-400 focus:ring-2 focus:ring-purple-400/20 transition-all duration-200 bg-white/80 text-gray-800"
                  required
                />
                <button
                  type="button"
                  onClick={() => setPasswordVisible(!passwordVisible)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-purple-500 transition-colors duration-200"
                >
                  {passwordVisible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Mobile Options */}
            <div className="flex items-center justify-between text-sm">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={rememberDevice}
                  onChange={() => setRememberDevice(!rememberDevice)}
                  className="w-3 h-3 text-purple-500 border-gray-300 rounded focus:ring-purple-400/20"
                />
                <span className="text-gray-600 font-medium">Remember me</span>
              </label>
              <button
                type="button"
                onClick={() => navigateTo('/forgot-password')}
                className="dynamic-site-text-1 font-medium hover:underline transition-colors duration-200 text-sm"
              >
                Forgot?
              </button>
            </div>

            {/* Mobile Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full dynamic-site-bg hover:opacity-90  text-white py-3 rounded-2xl font-semibold transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-[1.02] flex items-center justify-center gap-2 shadow-lg"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Signing In...
                </>
              ) : (
                <>
                  <LogIn className="w-4 h-4" />
                  Sign In
                </>
              )}
            </button>

            {/* Mobile Register Link */}
            <div className="text-center pt-2">
              <span className="text-gray-500 text-sm">New here? </span>
              <a href="/register" className="dynamic-site-text-3 font-semibold hover:text-purple-600 hover:underline transition-colors duration-200 text-sm">
                Join Us
              </a>
            </div>
          </form>

        
        </div>
      </div>

      {/* Desktop Layout - Optimized for small laptops */}
      <div className="hidden lg:flex relative h-screen items-center justify-center p-4 z-10">
        <div className="w-full max-w-5xl bg-white/60 backdrop-blur-2xl rounded-3xl shadow-xl overflow-hidden border border-white/40 h-[85vh] max-h-[600px]">
          <div className="flex h-full">
            {/* Left Panel - Community Features */}
            <div className="w-1/2 dynamic-site-bg  text-white relative overflow-hidden flex flex-col rounded-l-3xl">
              {/* Subtle Background Pattern */}
              <div className="absolute inset-0 opacity-10">
                <div className="absolute top-8 left-8 w-24 h-24 bg-white rounded-full blur-2xl"></div>
                <div className="absolute bottom-16 right-8 w-16 h-16 bg-purple-200 rounded-full blur-xl"></div>
                <div className="absolute top-1/2 right-16 w-12 h-12 bg-white rounded-full blur-lg"></div>
              </div>
              
              {/* Compact Logo Section */}
              <div className="relative z-10 flex items-center justify-center pt-8 pb-4">
                <div className="text-center">
                  <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mx-auto mb-2 border border-white/30">
                    <span className="text-white font-bold text-xl">C</span>
                  </div>
                  <h1 className="text-2xl font-bold mb-1">Communiticate</h1>
                  <p className="text-white/80 text-sm">Where Communities Thrive</p>
                </div>
              </div>

              {/* Compact Features Content */}
              <div className="relative z-10 flex-1 flex flex-col justify-center px-8 space-y-4">
                <div className="space-y-3">
                  <div className="flex items-center gap-3 transform hover:translate-x-1 transition-transform duration-300">
                    <div className="w-8 h-8 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/30">
                      <Users className="w-4 h-4" />
                    </div>
                    <div>
                      <h2 className="text-lg font-bold">CONNECT</h2>
                      <p className="text-white/70 text-xs">Build meaningful relationships</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3 transform hover:translate-x-1 transition-transform duration-300">
                    <div className="w-8 h-8 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/30">
                      <MessageCircle className="w-4 h-4" />
                    </div>
                    <div>
                      <h2 className="text-lg font-bold">ENGAGE</h2>
                      <p className="text-white/70 text-xs">Participate in discussions</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3 transform hover:translate-x-1 transition-transform duration-300">
                    <div className="w-8 h-8 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/30">
                      <Zap className="w-4 h-4" />
                    </div>
                    <div>
                      <h2 className="text-lg font-bold">GROW</h2>
                      <p className="text-white/70 text-xs">Expand your network</p>
                    </div>
                  </div>
                </div>
                
                {/* Compact Community Stats */}
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 border border-white/20">
                  <h3 className="text-sm font-semibold mb-3 text-center">Join Our Community</h3>
                  <div className="grid grid-cols-3 gap-3 text-center">
                    <div>
                      <div className="text-lg font-bold">50K+</div>
                      <div className="text-xs text-white/70">Members</div>
                    </div>
                    <div>
                      <div className="text-lg font-bold">1000+</div>
                      <div className="text-xs text-white/70">Communities</div>
                    </div>
                    <div>
                      <div className="text-lg font-bold">98%</div>
                      <div className="text-xs text-white/70">Satisfaction</div>
                    </div>
                  </div>
                </div>

                {/* Compact Trust Badges */}
                <div className="flex flex-wrap justify-center gap-3 text-xs">
                  <div className="flex items-center gap-1 text-white/60">
                    <Shield className="w-3 h-3 text-green-300" />
                    <span>Secure</span>
                  </div>
                  <div className="flex items-center gap-1 text-white/60">
                    <Globe className="w-3 h-3 text-blue-300" />
                    <span>Global</span>
                  </div>
                  <div className="flex items-center gap-1 text-white/60">
                    <Star className="w-3 h-3 text-yellow-300" />
                    <span>Top Rated</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Panel - Compact Login Form */}
            <div className="w-1/2 p-8 flex flex-col justify-center bg-gradient-to-br from-gray-50/80 to-white/80 rounded-r-3xl">
              <div className="max-w-sm mx-auto w-full space-y-6">
                {/* Compact Header */}
                <div className="text-center space-y-2">
                  <h1 className="text-xl font-bold text-gray-800">Welcome Back!</h1>
                  <div className="text-2xl font-bold dynamic-site-bg bg-clip-text text-transparent">
                    Sign In
                  </div>
                  <p className="text-gray-500 text-sm">Access your community</p>
                </div>

                {/* Compact Login Form */}
                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Email Field */}
                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-gray-700">Email</label>
                    <div className="relative group">
                      <Mail className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 transition-colors duration-200 ${emailFocused ? 'text-purple-500' : 'text-gray-400'}`} />
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        onFocus={() => setEmailFocused(true)}
                        onBlur={() => setEmailFocused(false)}
                        placeholder="Enter your email"
                        className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-2xl focus:outline-none focus:border-purple-400 focus:ring-2 focus:ring-purple-400/20 transition-all duration-200 bg-white/80 text-gray-800 group-hover:border-gray-300"
                        required
                      />
                    </div>
                  </div>

                  {/* Password Field */}
                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-gray-700">Password</label>
                    <div className="relative group">
                      <Lock className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 transition-colors duration-200 ${passwordFocused ? 'text-purple-500' : 'text-gray-400'}`} />
                      <input
                        type={passwordVisible ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        onFocus={() => setPasswordFocused(true)}
                        onBlur={() => setPasswordFocused(false)}
                        placeholder="Enter your password"
                        className="w-full pl-10 pr-10 py-3 border border-gray-200 rounded-2xl focus:outline-none focus:border-purple-400 focus:ring-2 focus:ring-purple-400/20 transition-all duration-200 bg-white/80 text-gray-800 group-hover:border-gray-300"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setPasswordVisible(!passwordVisible)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-purple-500 transition-colors duration-200"
                      >
                        {passwordVisible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  {/* Compact Options */}
                  <div className="flex items-center justify-between text-sm">
                    <label className="flex items-center space-x-2 cursor-pointer group">
                      <input
                        type="checkbox"
                        checked={rememberDevice}
                        onChange={() => setRememberDevice(!rememberDevice)}
                        className="w-3 h-3 text-purple-500 border-gray-300 rounded focus:ring-purple-400/20"
                      />
                      <span className="text-gray-600 font-medium group-hover:text-gray-800 transition-colors duration-200">Remember</span>
                    </label>
                    <button
                      type="button"
                      onClick={() => navigateTo('/forgot-password')}
                      className="dynamic-site-text-1 font-medium hover:underline transition-all duration-200"
                    >
                      Forgot Password?
                    </button>
                  </div>

                  {/* Compact Submit Button */}
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full dynamic-site-bg  hover:to-blue-600 text-white py-3 rounded-2xl font-semibold transition-all duration-300 transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-2 shadow-lg"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Signing In...
                      </>
                    ) : (
                      <>
                        <LogIn className="w-4 h-4" />
                        Access Community
                        <ArrowRight className="w-3 h-3" />
                      </>
                    )}
                  </button>

                  {/* Compact Register Link */}
                  <div className="text-center pt-2">
                    <p className="text-gray-500 text-sm">
                      New to our community?{' '}
                      <a 
                        href="/register" 
                        className="dynamic-site-text-1 font-semibold hover:underline transition-colors duration-200"
                      >
                        Join Us Today
                      </a>
                    </p>
                  </div>
                </form>

                {/* Compact Security & Trust */}
                <div className="pt-4 border-t border-gray-200">
                  <div className="flex items-center justify-center gap-2 text-xs text-gray-400">
                    <Shield className="w-3 h-3 text-green-400" />
                    <span>Secure & encrypted</span>
                  </div>
                  
                  <div className="flex items-center justify-center gap-4 text-xs text-gray-300 mt-2">
                    <div className="flex items-center gap-1">
                      <CheckCircle className="w-2 h-2 text-green-400" />
                      <span>SOC 2</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <CheckCircle className="w-2 h-2 text-blue-400" />
                      <span>GDPR</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <CheckCircle className="w-2 h-2 text-purple-400" />
                      <span>99.9% Uptime</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
